import Icon from "@ui-kit/icons/Icon.web";
import React from "react";
import Box, { type BoxProps } from "@ui-kit/layout/Box/Box.web";
import Text from "@ui-kit/typography/Text/Text.web";
import { AlertType, AlertSize } from "../alert-types";

const alertPaintMapBackground: Record<AlertType, string> = {
  info: "info.muted",
  error: "error.muted",
  warning: "warning.muted",
  success: "success.muted",
};

const alertPaintMapIcon: Record<AlertType, string> = {
  info: "info.fg",
  error: "error.fg",
  warning: "warning.fg",
  success: "success.fg",
};

const ALERT_SIZE_CONFIG = {
  xs: {
    height: "element.2xl",
    iconSize: "element.sm",
    fontSize: "xs" as const,
    paddingLeft: "2xs",
    paddingY: "2xs",
  },
  sm: {
    height: "element.3xl",
    iconSize: "element.md",
    fontSize: "md" as const,
    paddingLeft: "md",
    paddingY: "sm",
  },
  md: {
    height: "element.4xl",
    iconSize: "element.xl",
    fontSize: "md" as const,
    paddingLeft: "md",
    paddingY: "sm",
  },
};

const getIcon = (status: AlertType, size: AlertSize) => {
  const boxSize = ALERT_SIZE_CONFIG[size].iconSize;
  if (status === "error") {
    return <Icon.Error boxSize={boxSize} colorPalette="currentColor" />;
  }

  if (status === "warning") {
    return <Icon.Warning boxSize={boxSize} colorPalette="currentColor" />;
  }

  return <Icon.Info boxSize={boxSize} colorPalette="currentColor" />;
};

export type ToastAlertProps = {
  status: AlertType;
  title: React.ReactElement | string;
  size?: AlertSize;
  icon?: React.ReactElement;
  iconColor?: string;
  /** Padding between icon and title (default: md = 16px). Use "xs" for 8px. */
  titlePaddingStart?: BoxProps["paddingLeft"];
  /** Horizontal padding for banner content (default: lg = 20px). */
  paddingX?: BoxProps["paddingX"];
  /** Title font size. Use responsive object for mobile/desktop (e.g. { base: "sm", sm: "md" }). */
  titleFontSize?: BoxProps["fontSize"];
  isTransparent?: boolean;
};

const StaticAlert = ({
  status,
  title,
  size = "md",
  icon,
  iconColor: _iconColor,
  titlePaddingStart: _titlePaddingStart = "md",
  paddingX: _paddingX = "lg",
  titleFontSize: _titleFontSize = "md",
  isTransparent = false,
}: ToastAlertProps): JSX.Element => {
  const config = ALERT_SIZE_CONFIG[size];

  return (
    <Box
      display="flex"
      flexDirection="row"
      justifyContent="center"
      alignItems="center"
      backgroundColor={
        isTransparent ? "transparent" : alertPaintMapBackground[status]
      }
      paddingY={config.paddingY}
      height={config.height}
      css={{
        "& .chakra-icon": {
          color: alertPaintMapIcon[status],
        },
      }}
    >
      {icon || getIcon(status, size)}
      <Box
        display="flex"
        flexDirection="column"
        fontSize={config.fontSize}
        paddingLeft={config.paddingLeft}
      >
        <Text color="fg" as="span">
          {title}
        </Text>
      </Box>
    </Box>
  );
};

export default StaticAlert;
